import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import BigNumber from 'bignumber.js';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import config from '../shared/data/config.json';
import { ButtonActiveService } from '../shared/services/button-active.service';
import {
  NftStakeDataItem,
  NftStakingService,
} from '../shared/services/contracts/nft-staking.service';
import { TokenService } from '../shared/services/contracts/token.service';
import { ValidatorUtils } from '../shared/validation/validators';

@Component({
  selector: 'app-nft-staking',
  templateUrl: './nft-staking.component.html',
  styleUrls: ['./nft-staking.component.scss'],
})
export class NftStakingComponent implements OnInit, OnDestroy {
  tokenName = config.tokenName;
  isMobile = this.deviceSvc.isMobile();
  form!: UntypedFormGroup;

  stakingData?: Array<NftStakeDataItem>;
  tokenAmount = '0';

  lockFor: number[] = [0, 1, 2, 3, 4, 5];
  staking$: Observable<boolean> = this.buttonActiveSvc.staking$;
  unstaking$: Observable<boolean> = this.buttonActiveSvc.unstaking$;
  private destroy$ = new Subject();
  modalRef!: BsModalRef;
  exchangeList = config.exchangeList;

  isLoading = true;

  constructor(
    private modalSvc: BsModalService,
    private fb: UntypedFormBuilder,
    private deviceSvc: DeviceDetectorService,
    private buttonActiveSvc: ButtonActiveService,
    public tokenService: TokenService,
    public nftStakingService: NftStakingService
  ) {}

  ngOnInit(): void {
    this.nftStakingService.stakingData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          this.stakingData = data;
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        }
      );

    this.tokenService.tokenAmount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((amount) => {
        this.tokenAmount = amount;
        this.initForm();
      });

    this.initForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(): void {
    this.form = this.fb.group({
      available: [
        new BigNumber(this.tokenAmount)
          .multipliedBy(new BigNumber(10).pow(-12))
          .toString(),
        [
          ValidatorUtils.compareBigNumbers(this.tokenAmount),
          ValidatorUtils.validateDecimal,
          ValidatorUtils.minBigNumbers('1000'),
        ],
      ],
      staked: [
        '0',
        [
          ValidatorUtils.compareBigNumbers(this.totalAmount),
          ValidatorUtils.validateDecimal,
        ],
      ],
    });
  }

  stakeAll(): void {
    const value = this.tokenAmount;
    this.form
      .get('available')
      ?.setValue(
        new BigNumber(value).div(new BigNumber(10).pow(12)).toString()
      );
  }

  async stake(lockPeriod: number): Promise<any> {
    if (this.form.get('available')?.valid) {
      const value = new BigNumber(
        this.form.get('available')?.value
      ).multipliedBy(new BigNumber(10).pow(12));
      if (value.isGreaterThan(0)) {
        await this.nftStakingService.stakeToken(value, lockPeriod);
      }
    }
  }

  unstake(tokenId: string): void {
    this.nftStakingService.unstakeToken(tokenId);
  }

  openModal(template: TemplateRef<any>): void {
    this.modalRef = this.modalSvc.show(
      template,
      Object.assign({}, { class: 'staking modal-lg modal-dialog-centered' })
    );
  }

  get totalAmount(): string {
    return (
      this.stakingData
        ?.reduce((sum, stake) => sum.plus(stake.amount), new BigNumber(0))
        .toString() ?? '0'
    );
  }

  get totalRewards(): string {
    return (
      this.stakingData
        ?.reduce((sum, stake) => sum.plus(stake.reward), new BigNumber(0))
        .toString() ?? '0'
    );
  }

  goToUrl(url: string): void {
    window.open(url, '_blank');
  }

  isInFuture(date: Date): boolean {
    return date > new Date();
  }

  calcUSDCforLock(lock: number): number {
    const percentages = [0, 0.0005, 0.001];
    return percentages[lock] * this.form.get('available')?.value;
  }
}
